import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { loginSuccess } from '../redux/authSlice';
import apiClient from '../utils/apiClient';
import Logo from '../components/Logo';

export default function LoginPage() {
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.post('/auth/login/', form);
      const { token, user } = res.data;
      dispatch(loginSuccess({ token, user }));
      localStorage.setItem('multiauthor_token', token);
      if (plan) {
        navigate(`/profile?plan=${plan}`);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Invalid credentials.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', boxSizing: 'border-box',
    background: '#0D1117', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10, fontSize: 14, outline: 'none',
    color: 'white', fontFamily: 'Inter, sans-serif',
    transition: 'border-color 0.15s',
  };

  const shellLink = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: '#9CA3AF',
    textDecoration: 'none',
    fontSize: 13,
    fontWeight: 600,
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#0D1117',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Inter, sans-serif', padding: 24,
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 16, gap: 12,
        }}>
          <Link to="/" style={shellLink}>
            <ArrowLeft size={15} /> Home
          </Link>
          <Link to="/register" style={{ ...shellLink, color: '#F97316' }}>
            Need an account? Sign up
          </Link>
        </div>

        <div style={{
          background: '#1A2236', border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 20, padding: '40px 36px', width: '100%',
          boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <Logo size={48} />
            </div>
            <h1 style={{
              fontFamily: 'Syne, sans-serif', fontWeight: 900, fontSize: 26,
              color: 'white', margin: '0 0 8px', letterSpacing: '-0.02em',
            }}>
              Multi <span style={{ color: '#F97316' }}>Author</span>
            </h1>
            <p style={{ color: '#6B7280', fontSize: 14, margin: 0 }}>
              Sign in to your workspace
            </p>
          </div>

          {error && (
            <div style={{
              background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.3)',
              color: '#F87171', borderRadius: 10, padding: '10px 16px',
              fontSize: 13, marginBottom: 20,
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#9CA3AF', marginBottom: 6 }}>
                Username
              </label>
              <input
                value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })}
                placeholder="your username"
                required
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#F97316'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
              />
            </div>
            <div style={{ marginBottom: 28 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#9CA3AF', marginBottom: 6 }}>
                Password
              </label>
              <input
                type="password"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                required
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#F97316'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '13px',
                background: loading ? '#C2550A' : '#F97316',
                color: 'white', border: 'none', borderRadius: 10,
                fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'Syne, sans-serif', letterSpacing: '-0.01em',
                transition: 'background 0.15s',
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: 22, fontSize: 13, color: '#6B7280' }}>
            Don't have an account?{' '}
            <Link to="/register" style={{ color: '#F97316', fontWeight: 600, textDecoration: 'none' }}>
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
